import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useOutlet, OUTLETS } from "@/lib/outlet";
import { toast } from "sonner";
import {
  Plus, X, Trash2, CheckCircle2, XCircle, Send, ChevronRight,
} from "lucide-react";

const EMPTY_LINE = { item_id: "", item_name: "", unit: "kg", qty: 1, notes: "" };

/**
 * Indents — mobile-first raise-indent flow + approve queue + convert-to-PO.
 */
export default function IndentsTab() {
  const { outlet, outletParam } = useOutlet();
  const [indents, setIndents] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_approval");
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(false);
  const [selected, setSelected] = useState({}); // {id: true}

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (outletParam) params.outlet = outletParam;
      const [ind, it] = await Promise.all([
        api.get("/staff/indents", { params }),
        api.get("/staff/inventory/items"),
      ]);
      setIndents(ind.data);
      setItems(it.data);
    } catch {
      toast.error("Could not load indents");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [outletParam, statusFilter]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const toggleAll = () => {
    if (selectedIds.length === indents.length) setSelected({});
    else setSelected(Object.fromEntries(indents.map((i) => [i.id, true])));
  };

  const bulk = async (action, reason = "") => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post("/staff/indents/bulk-action", {
        indent_ids: selectedIds, action, reason,
      });
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${data.updated} indent${data.updated === 1 ? "" : "s"}`);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed");
    }
  };

  const convertToPOs = async () => {
    if (!outletParam) {
      toast.error("Pick a specific outlet first — Generate POs is per-outlet.");
      return;
    }
    if (!window.confirm(`Generate draft POs from approved indents for ${outlet}?`)) return;
    setConverting(true);
    try {
      const { data } = await api.post("/staff/indents/convert-to-pos",
        { outlet: outletParam });
      if (data.pos_created === 0) {
        toast.info(data.reason || "Nothing to convert");
      } else {
        toast.success(`Created ${data.pos_created} draft PO${data.pos_created === 1 ? "" : "s"}`);
      }
      setStatusFilter("converted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    } finally { setConverting(false); }
  };

  return (
    <div data-testid="indents-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex flex-wrap gap-1 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] rounded-full p-1">
          {["pending_approval", "approved", "converted", "rejected"].map((s) => (
            <button
              key={s}
              data-testid={`indent-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full transition-all ${
                statusFilter === s ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"
              }`}
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {statusFilter === "pending_approval" && selectedIds.length > 0 && (
            <>
              <button data-testid="indent-bulk-approve" onClick={() => bulk("approve")}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                <CheckCircle2 size={13} /> Approve {selectedIds.length}
              </button>
              <button data-testid="indent-bulk-reject" onClick={() => {
                const reason = window.prompt("Rejection reason (optional):") || "";
                bulk("reject", reason);
              }} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                <XCircle size={13} /> Reject {selectedIds.length}
              </button>
            </>
          )}
          {statusFilter === "approved" && indents.length > 0 && (
            <button data-testid="indent-convert" disabled={converting} onClick={convertToPOs}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded btn-brass !py-1.5 !px-3">
              <Send size={13} /> {converting ? "Generating…" : "Generate POs"}
            </button>
          )}
          <button data-testid="indent-new" onClick={() => setCreating(true)}
                  className="btn-brass !py-2 !px-4 text-sm">
            <Plus size={14} /> New indent
          </button>
        </div>
      </div>

      {loading && <div className="p-6 text-[hsl(var(--ink-soft))] text-sm">Loading…</div>}
      {!loading && indents.length === 0 && (
        <div className="p-10 text-center text-sm text-[hsl(var(--ink-soft))] border border-dashed border-[hsl(var(--rule))]">
          No indents in this queue.
        </div>
      )}

      {!loading && indents.length > 0 && (
        <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="p-2 text-xs text-[hsl(var(--ink-soft))] border-b border-[hsl(var(--rule))] flex items-center gap-2">
            {statusFilter === "pending_approval" && (
              <label className="flex items-center gap-1">
                <input type="checkbox" data-testid="indent-select-all"
                       checked={selectedIds.length === indents.length && indents.length > 0}
                       onChange={toggleAll} />
                Select all
              </label>
            )}
            <span className="ml-auto">{indents.length} indent{indents.length === 1 ? "" : "s"}</span>
          </div>
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {indents.map((ind) => (
              <li key={ind.id} data-testid={`indent-${ind.id}`} className="p-4">
                <div className="flex items-start gap-3">
                  {statusFilter === "pending_approval" && (
                    <input type="checkbox" data-testid={`indent-select-${ind.id}`}
                           checked={!!selected[ind.id]}
                           onChange={(e) => setSelected({ ...selected, [ind.id]: e.target.checked })} />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]">{ind.outlet}</span>
                      <span className="text-xs text-[hsl(var(--ink-soft))]">by {ind.created_by || "—"}</span>
                      <span className="text-xs text-[hsl(var(--ink-soft))]">· {new Date(ind.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {ind.note && <div className="mt-1 text-xs text-[hsl(var(--ink))]">Note: {ind.note}</div>}
                    <ul className="mt-2 text-sm space-y-1">
                      {ind.lines.map((l, i) => (
                        <li key={i} className="flex items-baseline gap-2">
                          <span className="font-medium">{l.item_name}</span>
                          <span className="text-[hsl(var(--ink-soft))]">— {l.qty} {l.unit}</span>
                          {l.notes && <span className="text-xs text-[hsl(var(--brass-2))] italic">({l.notes})</span>}
                        </li>
                      ))}
                    </ul>
                    {ind.rejection_reason && (
                      <div className="mt-2 text-xs text-[hsl(6_62%_45%)]">Rejected: {ind.rejection_reason}</div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {creating && (
        <IndentForm items={items} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function IndentForm({ items, onClose, onDone }) {
  const { outlet } = useOutlet();
  const defaultOutlet = outlet === "ALL" ? "TBB" : outlet;
  const [pickedOutlet, setPickedOutlet] = useState(defaultOutlet);
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async () => {
    const clean = lines.filter((l) => l.item_name.trim() && Number(l.qty) > 0);
    if (!clean.length) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      await api.post("/staff/indents", {
        outlet: pickedOutlet,
        lines: clean.map((l) => ({ ...l, qty: Number(l.qty) })),
        note,
      });
      toast.success("Indent submitted for approval");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="indent-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow">New indent</div>
            <h2 className="mt-1 display text-2xl">Raise indent</h2>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Outlet *
            <select data-testid="indent-outlet" value={pickedOutlet} onChange={(e) => setPickedOutlet(e.target.value)}
                    className="tbb-inp mt-1 w-full">
              {OUTLETS.filter((o) => o.code !== "ALL").map((o) => (
                <option key={o.code} value={o.code}>{o.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-5 border border-[hsl(var(--rule))]">
          <div className="p-2 flex items-center justify-between bg-[hsl(var(--bg-2))]">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Lines</div>
            <button data-testid="indent-add-line" type="button" onClick={addLine}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[hsl(var(--rule))]">
              <Plus size={12} /> Add line
            </button>
          </div>
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {lines.map((l, i) => (
              <li key={i} data-testid={`indent-line-${i}`} className="p-3 grid grid-cols-1 md:grid-cols-[1fr_100px_100px_1fr_auto] gap-2">
                <input list={`items-${i}`} placeholder="Item"
                       data-testid={`indent-line-item-${i}`}
                       value={l.item_name}
                       onChange={(e) => {
                         const found = items.find((it) => it.name.toLowerCase() === e.target.value.toLowerCase());
                         updateLine(i, {
                           item_name: e.target.value,
                           item_id: found?.id || "",
                           unit: found?.unit || l.unit,
                         });
                       }}
                       className="tbb-inp text-sm" />
                <datalist id={`items-${i}`}>
                  {items.map((it) => <option key={it.id} value={it.name}>{it.category}</option>)}
                </datalist>
                <input type="number" step="0.01" min="0.01" placeholder="Qty"
                       data-testid={`indent-line-qty-${i}`}
                       value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })}
                       className="tbb-inp text-sm text-right" />
                <input placeholder="Unit" value={l.unit}
                       onChange={(e) => updateLine(i, { unit: e.target.value })}
                       className="tbb-inp text-sm" />
                <input placeholder="Note (need by…)" value={l.notes}
                       onChange={(e) => updateLine(i, { notes: e.target.value })}
                       className="tbb-inp text-sm" />
                <button type="button" onClick={() => removeLine(i)}
                        className="text-[hsl(6_62%_45%)]"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        </div>

        <label className="mt-4 block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Overall note
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    className="tbb-inp mt-1 w-full resize-none" />
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button type="button" data-testid="indent-submit" disabled={saving} onClick={submit}
                  className="btn-brass !py-2.5 !px-5 text-sm">
            {saving ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    </div>
  );
}
